import React, { useEffect, useState } from 'react';
import { getStackApp } from '../utils/stackAuth';

async function fetchWithRetry(url, options, maxRetries = 3) {
  let lastErr;
  for (let i = 0; i < maxRetries; i++) {
    try {
      const res = await fetch(url, options);
      return res;
    } catch (err) {
      lastErr = err;
      if (i < maxRetries - 1) await new Promise(r => setTimeout(r, 500 * 2 ** i));
    }
  }
  throw lastErr;
}

export default function OAuthCallback({ onLogin }) {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function handleCallback() {
      try {
        const app = getStackApp();

        // Step 1: Process OAuth code & state from URL
        let success;
        try {
          success = await app.callOAuthCallback();
        } catch (e) {
          console.error('callOAuthCallback failed:', e);
          if (!cancelled) { setStatus('error'); setError('OAuth exchange failed. Please try again.'); }
          return;
        }

        if (!success) {
          if (!cancelled) {
            setStatus('error');
            setError('Authentication was not completed. Please try again.');
          }
          return;
        }

        // Step 2: Get the authenticated user from Stack Auth
        let user;
        try {
          user = await app.getUser();
        } catch (e) {
          console.error('getUser failed:', e);
          if (!cancelled) { setStatus('error'); setError('Could not retrieve user info. Please try again.'); }
          return;
        }
        if (!user || cancelled) {
          if (!cancelled) {
            setStatus('error');
            setError('Could not retrieve user info. Please try again.');
          }
          return;
        }

        // Step 3: Get access token
        let accessToken;
        try {
          const authJson = await user.getAuthJson();
          accessToken = authJson?.accessToken;
        } catch (e) {
          console.error('getAuthJson failed:', e);
          if (!cancelled) { setStatus('error'); setError('Could not retrieve auth token. Please try again.'); }
          return;
        }

        if (!accessToken) {
          setStatus('error');
          setError('Could not retrieve auth token.');
          return;
        }

        // Step 4: Exchange with our backend — ALWAYS use relative URL
        // so it hits the same domain regardless of REACT_APP_BACKEND_URL
        let res;
        try {
          res = await fetchWithRetry('/api/auth/stack-token', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ access_token: accessToken }),
          });
        } catch (e) {
          console.error('Backend stack-token call failed:', e);
          if (!cancelled) { setStatus('error'); setError('Could not reach the server. Please check your connection and try again.'); }
          return;
        }

        if (res.ok) {
          const userData = await res.json();
          if (userData.is_banned) {
            setStatus('error');
            setError(userData.ban_reason || 'Account suspended.');
            return;
          }
          if (userData.session_token) {
            localStorage.setItem('swell_session_token', userData.session_token);
          }
          if (onLogin) onLogin(userData);
        } else {
          const data = await res.json().catch(() => ({}));
          setStatus('error');
          setError(data.detail || 'Login failed. Please try again.');
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        if (!cancelled) {
          setStatus('error');
          setError(err?.message || 'Authentication error. Please try again.');
        }
      }
    }
    handleCallback();
    return () => { cancelled = true; };
  }, [onLogin]);

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg relative z-50">
        <div className="text-center space-y-4 p-6 max-w-sm">
          <div className="w-12 h-12 mx-auto rounded-full bg-red-500/10 flex items-center justify-center">
            <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </div>
          <p className="text-red-400 text-sm" data-testid="oauth-error">{error}</p>
          <button
            onClick={() => window.location.href = '/'}
            className="text-primary text-sm underline hover:opacity-80 transition-opacity"
            data-testid="oauth-back-btn"
          >
            Back to home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg relative z-50">
      <div className="text-center space-y-4 p-6">
        <div className="flex space-x-2 justify-center">
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 bg-primary rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
        <p className="text-muted-foreground text-sm" data-testid="oauth-processing">Completing sign-in...</p>
      </div>
    </div>
  );
}
